import { Box, Flex, Heading, ModalHeader, Text } from '@televet/kibble-ui';
import { formatMoney } from 'pages/Reporting/utils/formatMoney';
import React from 'react';
import { PetAvatar } from 'shared/components/Avatars';
import CareBadge from 'shared/components/Badges/CareBadge';
import { EnrollmentStatus, SidePanelParentPetDataFragment } from 'shared/types/graphql';
import { UsePetBenefitCareBenefit } from '../../../hooks/usePetBenefits';
import { MembershipPastDueAlert, MembershipPastDueAlertProps } from '../../MembershipPastDueAlert';

interface IPetInfoHeaderProps extends Partial<MembershipPastDueAlertProps> {
  title: string;
  pet: SidePanelParentPetDataFragment | undefined;
  originalInvoiceCost?: number;
  totalInvoiceSavings?: number;
  paymentDue?: number;
  currentStep: number;
  isWizardPaymentsFFEnabled?: boolean;
  isLastStepAndFFEnabled?: boolean;
  accountCredit: UsePetBenefitCareBenefit | undefined;
  percentDiscount: UsePetBenefitCareBenefit | undefined;
}

const PetInfoHeader = ({
  title,
  pet,
  originalInvoiceCost,
  totalInvoiceSavings,
  paymentDue,
  currentStep,
  isWizardPaymentsFFEnabled,
  isLastStepAndFFEnabled,
  accountCredit,
  percentDiscount,
  pastDueInfo,
  status,
  openBillingPortal,
}: IPetInfoHeaderProps): JSX.Element => {
  const accountCreditDiscountStep = !!accountCredit || !!percentDiscount ? 1 : 0;
  // if there are no accountCredit or percentDiscount, the total steps will be 3, unless there is a payment step (isWizardPaymentsFFEnabled) then add one extra
  const totalSteps = isWizardPaymentsFFEnabled ? 3 + accountCreditDiscountStep : 4 + accountCreditDiscountStep;
  const adjustedCurrentStep = currentStep > totalSteps ? totalSteps : currentStep;
  // the current step cant be greater than the total steps
  // This has always been pretty hard coded, work is needed to make this more dynamic

  const enrollment = pet?.organizationPet?.carePlanEnrollments.find(
    (enrollment) => enrollment.status === EnrollmentStatus.Active,
  );

  return (
    <ModalHeader p={0}>
      <Heading as="p" size="md" p={4}>
        {title}
      </Heading>
      <Flex bgColor="background.subtle" p={4} justify="space-between" fontFamily="body">
        <Flex alignItems="center" columnGap={1}>
          <PetAvatar
            speciesType={pet?.speciesType}
            name={pet?.name}
            photoUrl={pet?.photoUrl}
            isDeceased={pet?.computedIsDeceased}
            isActive={pet?.computedIsActive}
            size="lg"
          />
          <Flex direction="column">
            <Text as="p" size="lg" fontWeight="semibold">
              {!!pet?.name ? pet.name : ''}
            </Text>
            <Flex alignItems="center">
              <CareBadge isCareMember paymentStatus={enrollment?.careStripeSubscription?.paymentStatus} />
              <Text size="sm" ml={1} fontWeight="normal">
                {enrollment?.plan.flowAlias || enrollment?.plan.title}
              </Text>
            </Flex>
          </Flex>
        </Flex>
        {originalInvoiceCost !== undefined && (
          <Flex direction="column">
            <Text size="xs">Original</Text>
            <Text data-testid="wizard-original-amount" size="sm">
              {formatMoney(originalInvoiceCost * 100)}
            </Text>
          </Flex>
        )}
        {totalInvoiceSavings !== undefined && (
          <Flex direction="column">
            <Text size="xs" color={totalInvoiceSavings > 0 ? 'success.600' : 'neutral.600'}>
              Savings
            </Text>
            <Text
              data-testid="wizard-savings-amount"
              size="sm"
              color={totalInvoiceSavings > 0 ? 'success.600' : 'neutral.600'}
            >
              {formatMoney(totalInvoiceSavings * 100)}
            </Text>
          </Flex>
        )}
        {paymentDue !== undefined && (
          <Flex direction="column">
            <Text size="xs">Payment Due</Text>
            <Text data-testid="wizard-payment-due" size="sm" fontWeight="bold">
              {formatMoney(paymentDue * 100)}
            </Text>
          </Flex>
        )}
      </Flex>
      {!!pastDueInfo && !!openBillingPortal && adjustedCurrentStep === 1 && (
        <Box pt={2} px={6}>
          <MembershipPastDueAlert status={status} pastDueInfo={pastDueInfo} openBillingPortal={openBillingPortal} />
        </Box>
      )}
      {isLastStepAndFFEnabled === false && (
        <Flex px={6} pt={4}>
          <Text fontWeight="bold" size="xs" color="neutral.600">
            {`${adjustedCurrentStep}/${totalSteps}`}
          </Text>
        </Flex>
      )}
    </ModalHeader>
  );
};

export default PetInfoHeader;
